import API_BASE from "../config.js";
import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Dica as DicaOnboarding, BotaoDica } from "../components/Dica";
import { authFetch } from "../services/authFetch";
import { useTranslation } from "react-i18next";

// Backend usa snake_case; a tela usa camelCase internamente.
function daApi(row) {
  return {
    ...row,
    formaPagamento: row.forma_pagamento || "",
    centroCusto: row.centro_custo || "",
    osVinculo: row.os_vinculo || "",
    recorrente: !!row.recorrente,
    competencia: row.competencia || "",
  };
}
function paraApi(l) {
  return {
    id: l.id, tipo: l.tipo, descricao: l.descricao, categoria: l.categoria,
    valor: Number(l.valor) || 0, data: l.data, formaPagamento: l.formaPagamento,
    status: l.status, centroCusto: l.centroCusto, obs: l.obs,
    osVinculo: l.osVinculo, parcelas: l.parcelas, recorrente: !!l.recorrente,
    competencia: l.competencia,
  };
}


// Extrai tenant_id do JWT token - evita conflito entre tenants
function getTenantIdFromToken() {
  try {
    const token = (sessionStorage.getItem("access_token")||localStorage.getItem("access_token"));
    if (!token) return (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload.tenant_id || (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1");
  } catch { return (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1"; }
}

// Mesma logica de components/Layout.js: chave de cache de config isolada por tenant
function getConfigKey() {
  try {
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
    if (!token) return "saas_config";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return "saas_config_tenant_" + (payload.tenant_id || "1");
  } catch { return "saas_config"; }
}

const STORAGE_KEY_PREFIX = "saas_financeiro_";

const CATEGORIAS_RECEITA = [
  "Servico de OS", "Venda de Peca", "Venda de Produto",
  "Adiantamento", "Bonificacao", "Outros"
];
const CATEGORIAS_DESPESA = [
  "Fornecedor de Pecas", "Aluguel", "Salario", "Energia Eletrica",
  "Agua", "Internet", "Telefone", "Material de Escritorio",
  "Material de Limpeza", "Ferramenta/Equipamento", "Marketing",
  // DAS -- Documento de Arrecadacao do Simples Nacional. Entra ao lado de
  // "Imposto/Taxa" por ser da mesma natureza, mas separado, porque o dono
  // da oficina precisa enxergar o DAS sozinho no grafico de despesas.
  // Categoria e texto: os lancamentos ja gravados nao mudam, e o relatorio
  // agrupa por categoria dinamicamente, entao a nova entra sem mais nada.
  "DAS", "Imposto/Taxa", "Manutencao Predial", "Seguro", "Outros"
];
const FORMAS_PAGAMENTO = ["Dinheiro", "PIX", "Cartao Debito", "Cartao Credito", "Boleto", "Transferencia", "Cheque"];
const CENTROS_CUSTO = ["Mao de Obra", "Pecas/Produtos", "Custo Fixo", "Custo Variavel", "Administrativo", "Comercial"];
const STATUS_OPTIONS = ["Pago", "Pendente", "Vencido", "Parcelado", "Cancelado"];

const FORM_INICIAL = {
  tipo: "receita", descricao: "", categoria: "", valor: "",
  data: new Date().toLocaleDateString("pt-BR"),
  formaPagamento: "PIX", status: "Pago", centroCusto: "Mao de Obra",
  obs: "", osVinculo: "", parcelas: "1", recorrente: false,
  competencia: new Date().toLocaleDateString("pt-BR", { month: "2-digit", year: "numeric" }),
};

function gerarId() { return Date.now() + Math.random().toString(36).slice(2); }
// A coluna `data` e varchar e convive com tres formatos gravados em
// producao: dd/mm/aaaa (digitado na tela), aaaa-mm-dd (gerado
// automaticamente) e mm/dd/aaaa (uma linha em formato americano).
// A versao anterior so entendia o primeiro: string ISO virava Invalid Date
// e ia para um balde "Invalid Date" no grafico, e 08/21/2026 era lido como
// dia 8 do mes 21, caindo em setembro de 2027.
// Isto e leitura tolerante -- nenhum registro e reescrito.
function parseBR(d) {
  if (!d) return new Date();
  const txt = String(d).trim();

  // aaaa-mm-dd -- montado campo a campo de proposito: new Date("2026-09-05")
  // e interpretado como UTC e, num fuso negativo como o nosso, volta um dia.
  const iso = txt.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const br = txt.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (br) {
    const a = Number(br[1]), b = Number(br[2]), ano = Number(br[3]);
    // Desempate entre dd/mm e mm/dd: so o numero que nao cabe em 12 pode
    // dizer qual e qual. Empate fica em dd/mm, que e o que a tela grava.
    const americano = a <= 12 && b > 12;
    const dia = americano ? b : a;
    const mes = americano ? a : b;
    return new Date(ano, mes - 1, dia);
  }

  // Formato que ninguem reconhece continua invalido, como antes -- melhor
  // aparecer errado do que ser silenciosamente colocado numa data inventada.
  const [dia, mes, ano] = txt.split("/");
  return new Date(ano, mes - 1, dia);
}
function formatMoeda(v) {
  return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_COR = {
  "Pago":      { bg: "#22c55e22", cor: "#22c55e", dot: "#22c55e" },
  "Pendente":  { bg: "#eab30822", cor: "#eab308", dot: "#eab308" },
  "Vencido":   { bg: "#ef444422", cor: "#ef4444", dot: "#ef4444" },
  "Parcelado": { bg: "#3b82f622", cor: "#3b82f6", dot: "#3b82f6" },
  "Cancelado": { bg: "#64748b22", cor: "#64748b", dot: "#64748b" },
};

function Badge({ status }) {
  const c = STATUS_COR[status] || STATUS_COR["Pendente"];
  return (
    <span style={{ background: c.bg, borderRadius: 6, color: c.cor, fontSize: 11, fontWeight: 700, padding: "2px 8px" }}>
      {status}
    </span>
  );
}

function CardMetrica({ label, valor, cor, sub, icon }) {
  return (
    <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderLeft: "4px solid " + cor, borderRadius: 12, padding: "16px 20px" }}>
      <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ color: "var(--text2)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ color: cor, fontSize: 24, fontWeight: 800 }}>{formatMoeda(valor)}</div>
      {sub && <div style={{ color: "var(--text3)", fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Dica({ lancamentos }) {
  const { t } = useTranslation();
  if (!lancamentos.length) return null;
  const receitas = lancamentos.filter(l => l.tipo === "receita" && l.status === "Pago").reduce((a, l) => a + Number(l.valor), 0);
  const despesas = lancamentos.filter(l => l.tipo === "despesa" && l.status !== "Cancelado").reduce((a, l) => a + Number(l.valor), 0);
  const fixos = lancamentos.filter(l => l.tipo === "despesa" && ["Custo Fixo", "Aluguel", "Salario"].includes(l.centroCusto || l.categoria)).reduce((a, l) => a + Number(l.valor), 0);
  const pendentes = lancamentos.filter(l => l.status === "Pendente").reduce((a, l) => a + Number(l.valor), 0);
  const vencidos = lancamentos.filter(l => l.status === "Vencido").reduce((a, l) => a + Number(l.valor), 0);
  const margem = receitas > 0 ? ((receitas - despesas) / receitas * 100) : 0;
  const pctFixo = receitas > 0 ? (fixos / receitas * 100) : 0;
  const dicas = [];
  if (vencidos > 0) dicas.push({ cor: "#ef4444", icon: "!", txt: t("financeiro.dica_vencidos",{valor:formatMoeda(vencidos)}) });
  if (pendentes > 0) dicas.push({ cor: "#eab308", icon: "?", txt: t("financeiro.dica_pendentes",{valor:formatMoeda(pendentes)}) });
  if (margem < 20 && receitas > 0) dicas.push({ cor: "#f97316", icon: "$", txt: t("financeiro.dica_margem_baixa",{margem:margem.toFixed(1)}) });
  if (pctFixo > 60 && receitas > 0) dicas.push({ cor: "#f97316", icon: "$", txt: t("financeiro.dica_custos_fixos",{pct:pctFixo.toFixed(0)}) });
  if (margem >= 30) dicas.push({ cor: "#22c55e", icon: "+", txt: t("financeiro.dica_excelente_margem") });
  if (!lancamentos.length) dicas.push({ cor: "#3b82f6", icon: "i", txt: t("financeiro.dica_sem_lancamentos") });
  return (
    <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 12, padding: 16 }}>
      {dicas.map((d, i) => (
        <div key={i} style={{ alignItems: "flex-start", background: d.cor + "11", border: "1px solid " + d.cor + "33", borderRadius: 8, display: "flex", gap: 8, marginBottom: 8, padding: "8px 12px" }}>
          <span style={{ color: "var(--text)", fontSize: 12, lineHeight: 1.5 }}>{d.txt}</span>
        </div>
      ))}
    </div>
  );
}

function GraficoBarras({ lancamentos }) {
  const { t } = useTranslation();
  const meses = {};
  lancamentos.forEach(l => {
    const d = parseBR(l.data);
    const key = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
    if (!meses[key]) meses[key] = { receita: 0, despesa: 0 };
    if (l.tipo === "receita" && l.status === "Pago") meses[key].receita += Number(l.valor);
    if (l.tipo === "despesa" && l.status !== "Cancelado") meses[key].despesa += Number(l.valor);
  });
  const dados = Object.entries(meses).slice(-6);
  if (!dados.length) return null;
  const maxVal = Math.max(...dados.map(([, v]) => Math.max(v.receita, v.despesa)), 1);
  return (
    <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 12, padding: 16 }}>
      <div style={{ color: "var(--text2)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 14, textTransform: "uppercase" }}>{t("financeiro.receitas_x_despesas")}</div>
      <div style={{ alignItems: "flex-end", display: "flex", gap: 12, height: 120, justifyContent: "space-around" }}>
        {dados.map(([mes, v]) => (
          <div key={mes} style={{ alignItems: "center", display: "flex", flex: 1, flexDirection: "column", gap: 4 }}>
            <div style={{ alignItems: "flex-end", display: "flex", gap: 3, height: 90, width: "100%" }}>
              <div style={{ background: "#22c55e", borderRadius: "3px 3px 0 0", flex: 1, height: Math.max(4, (v.receita / maxVal) * 90) + "px", minHeight: 4 }} title={t("financeiro.entradas") + ": " + formatMoeda(v.receita)} />
              <div style={{ background: "#ef4444", borderRadius: "3px 3px 0 0", flex: 1, height: Math.max(4, (v.despesa / maxVal) * 90) + "px", minHeight: 4 }} title={t("financeiro.saidas") + ": " + formatMoeda(v.despesa)} />
            </div>
            <div style={{ color: "var(--text3)", fontSize: 10, textAlign: "center" }}>{mes}</div>
          </div>
        ))}
      </div>
      <div style={{ alignItems: "center", display: "flex", gap: 16, marginTop: 8 }}>
        <div style={{ alignItems: "center", display: "flex", gap: 4 }}><div style={{ background: "#22c55e", borderRadius: 2, height: 10, width: 14 }} /><span style={{ color: "var(--text3)", fontSize: 11 }}>{t("financeiro.entradas")}</span></div>
        <div style={{ alignItems: "center", display: "flex", gap: 4 }}><div style={{ background: "#ef4444", borderRadius: 2, height: 10, width: 14 }} /><span style={{ color: "var(--text3)", fontSize: 11 }}>{t("financeiro.saidas")}</span></div>
      </div>
    </div>
  );
}

export default function Financeiro() {
  const { t } = useTranslation();
  const [lancamentos, setLancamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalDetalhe, setModalDetalhe] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroCategoria, setFiltroCategoria] = useState("todas");
  const [busca, setBusca] = useState("");
  const [tab, setTab] = useState("lancamentos");
  const [msg, setMsg] = useState("");

  const getTenantId = () => {
    const cfg = JSON.parse(localStorage.getItem(getConfigKey()) || "{}");
    return getTenantIdFromToken() || cfg.tenant_id || cfg.id || "1";
  };
  const storageKey = STORAGE_KEY_PREFIX + getTenantId();

  useEffect(() => {
    const tenantId = getTenantId();
    // Sem dados de demonstracao: se o banco responder (mesmo vazio), ele e a
    // fonte de verdade. So usamos o cache local se a requisicao falhar de fato
    // (ex: sem internet), nunca para mascarar uma lista realmente vazia.
    const usarCacheSeExistir = () => {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) { setLancamentos(JSON.parse(saved)); return; }
      } catch {}
      setLancamentos([]);
    };
    authFetch(API_BASE + "/api/v1/dados/financeiro/" + tenantId, {})
      .then(r => r.ok ? r.json() : null)
      .then(dados => {
        if (Array.isArray(dados)) {
          const mapeado = dados.map(daApi);
          setLancamentos(mapeado);
          localStorage.setItem(storageKey, JSON.stringify(mapeado));
        } else {
          usarCacheSeExistir();
        }
      })
      .catch(usarCacheSeExistir)
      .finally(() => setCarregando(false));
  }, [storageKey]);

  const salvarLancamentos = useCallback((lista) => {
    setLancamentos(lista);
    localStorage.setItem(storageKey, JSON.stringify(lista));
  }, [storageKey]);

  // Um id "real" (vindo do banco) e sempre numerico; ids de itens demo/cache
  // local (gerarId()) misturam timestamp com string aleatoria.
  const idReal = (id) => /^\d+$/.test(String(id));

  const apiSalvarLancamento = async (lanc) => {
    try {
      const r = await authFetch(API_BASE + "/api/v1/dados/financeiro/" + getTenantId(), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paraApi(lanc))
      });
      if (r.ok) return r.json();
    } catch {}
    return null;
  };

  const apiExcluirLancamento = async (id) => {
    if (!idReal(id)) return;
    try {
      await authFetch(API_BASE + "/api/v1/dados/financeiro/" + getTenantId() + "/" + id, { method: "DELETE" });
    } catch {}
  };

  const upd = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const salvarLancamento = () => {
    if (!form.descricao || !form.valor || !form.categoria) return;
    const novo = { ...form, valor: Number(form.valor) };
    setForm(FORM_INICIAL);
    setModalNovo(false);
    setMsg(t("financeiro.lancamento_salvo"));
    setTimeout(() => setMsg(""), 2500);
    apiSalvarLancamento(novo).then(res => {
      const id = res && res.id ? res.id : gerarId();
      const comId = { ...novo, id };
      setLancamentos(l => {
        const nova = [comId, ...l];
        localStorage.setItem(storageKey, JSON.stringify(nova));
        return nova;
      });
    });
  };

  const excluir = (id) => {
    if (!window.confirm(t("financeiro.confirm_excluir"))) return;
    salvarLancamentos(lancamentos.filter(l => l.id !== id));
    apiExcluirLancamento(id);
    setModalDetalhe(null);
  };

  const alterarStatus = (id, status) => {
    const atualizado = lancamentos.find(l => l.id === id);
    if (atualizado && idReal(id)) apiSalvarLancamento({ ...atualizado, status });
    salvarLancamentos(lancamentos.map(l => l.id === id ? { ...l, status } : l));
  };

  const filtrados = lancamentos.filter(l => {
    if (filtroTipo !== "todos" && l.tipo !== filtroTipo) return false;
    if (filtroStatus !== "todos" && l.status !== filtroStatus) return false;
    if (filtroCategoria !== "todas" && l.categoria !== filtroCategoria) return false;
    if (busca && !l.descricao?.toLowerCase().includes(busca.toLowerCase()) && !l.categoria?.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  });

  const totReceita = lancamentos.filter(l => l.tipo === "receita" && l.status === "Pago").reduce((a, l) => a + Number(l.valor), 0);
  const totDespesa = lancamentos.filter(l => l.tipo === "despesa" && l.status !== "Cancelado").reduce((a, l) => a + Number(l.valor), 0);
  const saldoLiquido = totReceita - totDespesa;
  const totPendente = lancamentos.filter(l => l.status === "Pendente").reduce((a, l) => a + Number(l.valor), 0);
  const totVencido = lancamentos.filter(l => l.status === "Vencido").reduce((a, l) => a + Number(l.valor), 0);
  const CATEGORIAS_ATUAL = form.tipo === "receita" ? CATEGORIAS_RECEITA : CATEGORIAS_DESPESA;

  const s = {
    label: { color: "var(--text2)", display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 5, textTransform: "uppercase" },
    input: { width: "100%", fontSize: 13 },
    select: { width: "100%", fontSize: 13 },
    row2: { display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" },
    row3: { display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 1fr" },
  };

  return (
    <Layout>
      <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 1100, margin: "0 auto" }}>

        <div style={{ alignItems: "flex-start", display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{alignItems:"center",display:"flex",gap:8}}><h1 style={{ fontSize: 22, fontWeight: 700 }}>{t("financeiro.titulo")}</h1><BotaoDica id="financeiro" /></div>
            <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 3 }}>{t("financeiro.subtitulo")}</p>
          </div>
          <button onClick={() => { setForm(FORM_INICIAL); setModalNovo(true); }}
            style={{ background: "var(--accent)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, padding: "10px 20px" }}>
            {t("financeiro.novo_lancamento")}
          </button>
        </div>

        <DicaOnboarding id="financeiro" jaUsou={lancamentos.length > 0} texto={t("dicas.financeiro")} />

        {msg && <div style={{ background: "#22c55e22", border: "1px solid #22c55e55", borderRadius: 8, color: "#4ade80", fontSize: 13, fontWeight: 600, padding: "10px 16px" }}>{msg}</div>}

        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(4, 1fr)" }}>
          <CardMetrica label={t("financeiro.card_entradas_pagas")} valor={totReceita} cor="#22c55e" icon="+" sub={t("financeiro.lancamentos_sub",{count:lancamentos.filter(l => l.tipo === "receita" && l.status === "Pago").length})} />
          <CardMetrica label={t("financeiro.card_saidas")} valor={totDespesa} cor="#ef4444" icon="-" sub={t("financeiro.lancamentos_sub",{count:lancamentos.filter(l => l.tipo === "despesa" && l.status !== "Cancelado").length})} />
          <CardMetrica label={t("financeiro.card_saldo_liquido")} valor={saldoLiquido} cor={saldoLiquido >= 0 ? "#3b82f6" : "#ef4444"} icon="=" sub={saldoLiquido >= 0 ? t("financeiro.saldo_positivo") : t("financeiro.saldo_negativo")} />
          <CardMetrica label={t("financeiro.card_pendente_vencido")} valor={totPendente + totVencido} cor="#eab308" icon="!" sub={totVencido > 0 ? t("financeiro.vencidos_sub",{valor:formatMoeda(totVencido)}) : t("financeiro.sem_vencidos")} />
        </div>

        <div style={{ borderBottom: "1px solid var(--border)", display: "flex", gap: 2 }}>
          {[{ id: "lancamentos", l: t("financeiro.tab_lancamentos") }, { id: "analise", l: t("financeiro.tab_analise") }].map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)} style={{ background: "none", border: "none", borderBottom: tab === tb.id ? "2px solid var(--accent)" : "2px solid transparent", color: tab === tb.id ? "var(--accent)" : "var(--text2)", cursor: "pointer", fontSize: 13, fontWeight: tab === tb.id ? 700 : 400, padding: "10px 18px" }}>{tb.l}</button>
          ))}
        </div>

        {tab === "lancamentos" && (
          <div>
            <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              <input value={busca} onChange={e => setBusca(e.target.value)} placeholder={t("financeiro.buscar_placeholder")} style={{ flex: 1, fontSize: 13, minWidth: 200 }} />
              <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ fontSize: 13 }}>
                <option value="todos">{t("financeiro.todos_tipos")}</option>
                <option value="receita">{t("financeiro.receitas")}</option>
                <option value="despesa">{t("financeiro.despesas")}</option>
              </select>
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ fontSize: 13 }}>
                <option value="todos">{t("financeiro.todos_status")}</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={{ fontSize: 13 }}>
                <option value="todas">{t("financeiro.todas_categorias")}</option>
                {[...CATEGORIAS_RECEITA, ...CATEGORIAS_DESPESA].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead>
                  <tr style={{ background: "var(--bg2)", borderBottom: "1px solid var(--border)" }}>
                    {[t("financeiro.col_data"), t("financeiro.col_descricao"), t("financeiro.col_categoria"), t("financeiro.col_forma_pag"), t("financeiro.col_centro_custo"), t("financeiro.col_valor"), t("financeiro.col_status"), t("financeiro.col_acoes")].map(h => (
                      <th key={h} style={{ color: "var(--text2)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", padding: "10px 14px", textAlign: "left", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {carregando ? (
                    <tr><td colSpan={8} style={{ color: "var(--text3)", fontSize: 13, padding: "24px", textAlign: "center" }}>{t("common.carregando")}</td></tr>
                  ) : filtrados.length === 0 ? (
                    <tr><td colSpan={8} style={{ color: "var(--text3)", fontSize: 13, padding: "24px", textAlign: "center" }}>{t("financeiro.nenhum_lancamento")}</td></tr>
                  ) : filtrados.map((l, i) => (
                    <tr key={l.id} style={{ background: i % 2 === 0 ? "transparent" : "var(--bg2)", borderBottom: "1px solid var(--border)" }}>
                      <td style={{ color: "var(--text3)", fontSize: 12, padding: "10px 14px", whiteSpace: "nowrap" }}>{l.data}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{l.descricao}</div>
                        {l.osVinculo && <div style={{ color: "var(--accent)", fontSize: 11 }}>OS: {l.osVinculo}</div>}
                        {l.obs && <div style={{ color: "var(--text3)", fontSize: 11 }}>{l.obs}</div>}
                        {l.recorrente && <span style={{ background: "#3b82f622", borderRadius: 4, color: "#3b82f6", fontSize: 10, padding: "1px 6px" }}>{t("financeiro.recorrente_badge")}</span>}
                      </td>
                      <td style={{ color: "var(--text2)", fontSize: 12, padding: "10px 14px" }}>{l.categoria}</td>
                      <td style={{ color: "var(--text3)", fontSize: 12, padding: "10px 14px" }}>{l.formaPagamento}</td>
                      <td style={{ color: "var(--text3)", fontSize: 12, padding: "10px 14px" }}>{l.centroCusto}</td>
                      <td style={{ fontSize: 13, fontWeight: 700, padding: "10px 14px", whiteSpace: "nowrap", color: l.tipo === "receita" ? "#22c55e" : "#ef4444" }}>
                        {l.tipo === "receita" ? "+" : "-"} {formatMoeda(l.valor)}
                      </td>
                      <td style={{ padding: "10px 14px" }}><Badge status={l.status} /></td>
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => setModalDetalhe(l)} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text2)", cursor: "pointer", fontSize: 11, padding: "4px 8px" }}>{t("financeiro.ver")}</button>
                          <button onClick={() => excluir(l.id)} style={{ background: "#ef444415", border: "1px solid #ef444440", borderRadius: 6, color: "#ef4444", cursor: "pointer", fontSize: 11, padding: "4px 8px" }}>{t("financeiro.del")}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ borderTop: "1px solid var(--border)", color: "var(--text3)", fontSize: 12, padding: "10px 14px" }}>
                {t("financeiro.lancamentos_exibidos",{count:filtrados.length})}
              </div>
            </div>
          </div>
        )}

        {tab === "analise" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Dica lancamentos={lancamentos} />
            <GraficoBarras lancamentos={lancamentos} />
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
              <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 12, padding: 16 }}>
                <div style={{ color: "var(--text2)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 12, textTransform: "uppercase" }}>{t("financeiro.top_categorias_despesa")}</div>
                {Object.entries(lancamentos.filter(l => l.tipo === "despesa" && l.status !== "Cancelado").reduce((acc, l) => { acc[l.categoria] = (acc[l.categoria] || 0) + Number(l.valor); return acc; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([cat, val]) => {
                  const pct = totDespesa > 0 ? (val / totDespesa * 100) : 0;
                  return (
                    <div key={cat} style={{ marginBottom: 10 }}>
                      <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12 }}>{cat}</span>
                        <span style={{ color: "#ef4444", fontSize: 12, fontWeight: 700 }}>{formatMoeda(val)} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div style={{ background: "var(--bg2)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                        <div style={{ background: "#ef4444", borderRadius: 4, height: "100%", width: pct + "%" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 12, padding: 16 }}>
                <div style={{ color: "var(--text2)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 12, textTransform: "uppercase" }}>{t("financeiro.resumo_forma_pagamento")}</div>
                {Object.entries(lancamentos.filter(l => l.tipo === "receita" && l.status === "Pago").reduce((acc, l) => { acc[l.formaPagamento] = (acc[l.formaPagamento] || 0) + Number(l.valor); return acc; }, {})).sort((a, b) => b[1] - a[1]).map(([forma, val]) => {
                  const pct = totReceita > 0 ? (val / totReceita * 100) : 0;
                  return (
                    <div key={forma} style={{ marginBottom: 10 }}>
                      <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12 }}>{forma}</span>
                        <span style={{ color: "#22c55e", fontSize: 12, fontWeight: 700 }}>{formatMoeda(val)} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div style={{ background: "var(--bg2)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                        <div style={{ background: "#22c55e", borderRadius: 4, height: "100%", width: pct + "%" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      </div>

      {modalNovo && (
        <div style={{ alignItems: "flex-start", background: "rgba(0,0,0,0.75)", bottom: 0, display: "flex", justifyContent: "center", left: 0, overflowY: "auto", padding: 20, position: "fixed", right: 0, top: 0, zIndex: 9999 }}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, maxHeight: "calc(100vh - 40px)", maxWidth: 640, overflowY: "auto", padding: 32, width: "100%", margin: "20px 0" }}>
            <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t("financeiro.modal_novo_titulo")}</div>
              <button onClick={() => setModalNovo(false)} style={{ background: "var(--bg3)", border: "none", borderRadius: 8, color: "var(--text2)", cursor: "pointer", fontSize: 18, padding: "4px 10px" }}>x</button>
            </div>
            <div style={{ display: "grid", gap: 4, gridTemplateColumns: "1fr 1fr", marginBottom: 16 }}>
              {["receita", "despesa"].map(tp => (
                <button key={tp} onClick={() => { upd("tipo", tp); upd("categoria", ""); }}
                  style={{ background: form.tipo === tp ? (tp === "receita" ? "#22c55e" : "#ef4444") : "var(--bg3)", border: "2px solid " + (form.tipo === tp ? (tp === "receita" ? "#22c55e" : "#ef4444") : "var(--border)"), borderRadius: 8, color: form.tipo === tp ? "#fff" : "var(--text2)", cursor: "pointer", fontSize: 13, fontWeight: 700, padding: "10px 0", textTransform: "capitalize" }}>
                  {tp === "receita" ? t("financeiro.receita_tab") : t("financeiro.despesa_tab")}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={s.label}>{t("financeiro.descricao")}</label>
                <input style={s.input} value={form.descricao} onChange={e => upd("descricao", e.target.value)} placeholder={t("financeiro.descricao_placeholder")} />
              </div>
              <div style={s.row2}>
                <div>
                  <label style={s.label}>{t("financeiro.categoria")}</label>
                  <select style={s.select} value={form.categoria} onChange={e => upd("categoria", e.target.value)}>
                    <option value="">{t("financeiro.selecione")}</option>
                    {CATEGORIAS_ATUAL.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>{t("financeiro.centro_custo")}</label>
                  <select style={s.select} value={form.centroCusto} onChange={e => upd("centroCusto", e.target.value)}>
                    {CENTROS_CUSTO.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.row3}>
                <div>
                  <label style={s.label}>{t("financeiro.valor")}</label>
                  <input type="number" style={s.input} value={form.valor} onChange={e => upd("valor", e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div>
                  <label style={s.label}>{t("financeiro.data")}</label>
                  <input style={s.input} value={form.data} onChange={e => upd("data", e.target.value)} placeholder={t("financeiro.data_placeholder")} />
                </div>
                <div>
                  <label style={s.label}>{t("financeiro.competencia")}</label>
                  <input style={s.input} value={form.competencia} onChange={e => upd("competencia", e.target.value)} placeholder={t("financeiro.competencia_placeholder")} />
                </div>
              </div>
              <div style={s.row3}>
                <div>
                  <label style={s.label}>{t("financeiro.forma_pagamento")}</label>
                  <select style={s.select} value={form.formaPagamento} onChange={e => upd("formaPagamento", e.target.value)}>
                    {FORMAS_PAGAMENTO.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>{t("financeiro.status")}</label>
                  <select style={s.select} value={form.status} onChange={e => upd("status", e.target.value)}>
                    {STATUS_OPTIONS.map(so => <option key={so} value={so}>{so}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>{t("financeiro.parcelas")}</label>
                  <select style={s.select} value={form.parcelas} onChange={e => upd("parcelas", e.target.value)}>
                    {["1","2","3","4","5","6","8","10","12","18","24"].map(n => <option key={n} value={n}>{n}x</option>)}
                  </select>
                </div>
              </div>
              <div style={s.row2}>
                <div>
                  <label style={s.label}>{t("financeiro.vinculo_os")}</label>
                  <input style={s.input} value={form.osVinculo} onChange={e => upd("osVinculo", e.target.value)} placeholder={t("financeiro.vinculo_os_placeholder")} />
                </div>
                <div style={{ alignItems: "center", display: "flex", gap: 10, marginTop: 20 }}>
                  <input type="checkbox" id="recorrente" checked={form.recorrente} onChange={e => upd("recorrente", e.target.checked)} style={{ height: 16, width: 16 }} />
                  <label htmlFor="recorrente" style={{ color: "var(--text)", cursor: "pointer", fontSize: 13 }}>{t("financeiro.lancamento_recorrente")}</label>
                </div>
              </div>
              <div>
                <label style={s.label}>{t("financeiro.observacoes")}</label>
                <textarea value={form.obs} onChange={e => upd("obs", e.target.value)} rows={2} placeholder={t("financeiro.observacoes_placeholder")} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontFamily: "inherit", fontSize: 13, outline: "none", padding: "8px 12px", resize: "vertical", width: "100%" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setModalNovo(false)} style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text2)", cursor: "pointer", fontSize: 13, padding: "10px 20px" }}>{t("common.cancelar")}</button>
              <button onClick={salvarLancamento} style={{ background: form.tipo === "receita" ? "#22c55e" : "#ef4444", border: "none", borderRadius: 8, color: form.tipo === "receita" ? "#000" : "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, padding: "10px 24px" }}>
                {t("financeiro.salvar_tipo",{tipo: form.tipo === "receita" ? t("financeiro.receita_tab") : t("financeiro.despesa_tab")})}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalDetalhe && (
        <div style={{ alignItems: "center", background: "rgba(0,0,0,0.75)", bottom: 0, display: "flex", justifyContent: "center", left: 0, padding: 20, position: "fixed", right: 0, top: 0, zIndex: 9999 }}>
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, maxWidth: 500, padding: 32, width: "100%" }}>
            <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t("financeiro.modal_detalhe_titulo")}</div>
              <button onClick={() => setModalDetalhe(null)} style={{ background: "var(--bg3)", border: "none", borderRadius: 8, color: "var(--text2)", cursor: "pointer", fontSize: 18, padding: "4px 10px" }}>x</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {[
                [t("financeiro.det_tipo"), modalDetalhe.tipo === "receita" ? t("financeiro.receita_tab") : t("financeiro.despesa_tab")],
                [t("financeiro.det_descricao"), modalDetalhe.descricao],
                [t("financeiro.det_valor"), formatMoeda(modalDetalhe.valor)],
                [t("financeiro.det_data"), modalDetalhe.data],
                [t("financeiro.det_competencia"), modalDetalhe.competencia],
                [t("financeiro.det_categoria"), modalDetalhe.categoria],
                [t("financeiro.det_centro_custo"), modalDetalhe.centroCusto],
                [t("financeiro.det_forma_pagamento"), modalDetalhe.formaPagamento],
                [t("financeiro.det_parcelas"), modalDetalhe.parcelas + "x"],
                [t("financeiro.det_os_vinculada"), modalDetalhe.osVinculo || "-"],
                [t("financeiro.det_recorrente"), modalDetalhe.recorrente ? t("common.sim") : t("common.nao")],
                [t("financeiro.det_observacoes"), modalDetalhe.obs || "-"],
              ].map(([label, valor]) => (
                <div key={label} style={{ alignItems: "center", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", paddingBottom: 8 }}>
                  <span style={{ color: "var(--text2)", fontSize: 12 }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{valor}</span>
                </div>
              ))}
              <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", paddingBottom: 8 }}>
                <span style={{ color: "var(--text2)", fontSize: 12 }}>{t("financeiro.det_status")}</span>
                <Badge status={modalDetalhe.status} />
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", marginTop: 20 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {STATUS_OPTIONS.filter(so => so !== modalDetalhe.status).map(so => (
                  <button key={so} onClick={() => { alterarStatus(modalDetalhe.id, so); setModalDetalhe(l => ({ ...l, status: so })); }}
                    style={{ background: STATUS_COR[so]?.bg || "var(--bg3)", border: "1px solid " + (STATUS_COR[so]?.cor || "var(--border)"), borderRadius: 6, color: STATUS_COR[so]?.cor || "var(--text2)", cursor: "pointer", fontSize: 11, padding: "5px 10px" }}>
                    {so}
                  </button>
                ))}
              </div>
              <button onClick={() => excluir(modalDetalhe.id)} style={{ background: "#ef444415", border: "1px solid #ef4444", borderRadius: 8, color: "#ef4444", cursor: "pointer", fontSize: 12, padding: "8px 16px" }}>{t("common.excluir")}</button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
